/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Wizard Step 2: SEO Title Generation & Selection View (Task 10.2.3 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useMemo } from 'react';
import {
  useLongformWizardStore,
  calculateTitleSeoScore,
} from '../../stores/useLongformWizardStore';
import { useEditorStore } from '../../stores/useEditorStore';
import { useI18n } from '../../stores/useI18nStore';
import {
  Sparkles,
  RotateCcw,
  CheckCircle2,
  Circle,
  ArrowRight,
  ArrowLeft,
  FileEdit,
  Award,
  Zap,
} from 'lucide-react';

interface Step2TitlesViewProps {
  onPushToEditor?: () => void;
}

export const Step2TitlesView: React.FC<Step2TitlesViewProps> = ({ onPushToEditor }) => {
  const {
    setup,
    generatedTitles,
    selectedTitleId,
    customTitle,
    isGeneratingTitles,
    selectTitle,
    setCustomTitle,
    generateTitles,
    setStep,
    markStepCompleted,
    getSelectedFinalTitle,
  } = useLongformWizardStore();

  const { setTitle: setEditorTitle } = useEditorStore();
  const { t } = useI18n();

  // Calculate live SEO score for custom edited title
  const liveScore = useMemo(() => {
    return calculateTitleSeoScore(customTitle, setup.primaryKeyword);
  }, [customTitle, setup.primaryKeyword]);

  const handleSelectTitleCard = (id: string) => {
    selectTitle(id);
  };

  const handleContinueToStep3 = () => {
    const finalTitle = getSelectedFinalTitle();
    if (!finalTitle) return;

    // Sync title to Editor store
    setEditorTitle(finalTitle);
    markStepCompleted(2);
    if (useLongformWizardStore.getState().headings.length === 0) {
      useLongformWizardStore.getState().generateOutlines();
    }
    setStep(3);
  };

  const handlePushDirectlyToEditor = () => {
    const finalTitle = getSelectedFinalTitle();
    if (!finalTitle) return;

    setEditorTitle(finalTitle);
    if (onPushToEditor) {
      onPushToEditor();
    }
  };

  const getScoreBadge = (score: number) => {
    if (score >= 90) {
      return {
        bg: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
        label: t('wizard.step2Details.scoreExcellent'),
      };
    }
    if (score >= 80) {
      return {
        bg: 'bg-primary/10 text-primary border-primary/30',
        label: t('wizard.step2Details.scoreGreat'),
      };
    }
    return {
      bg: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
      label: t('wizard.step2Details.scoreGood'),
    };
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* 1. Header with Regeneration CTA */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border border-surface-border bg-surface shadow-glass">
        <div>
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-accent" />
            <h3 className="font-heading font-bold text-lg text-foreground">
              {t('wizard.step2Details.sectionTitle')}
            </h3>
          </div>
          <p className="text-xs text-foreground-muted mt-1">
            {t('wizard.step2Details.sectionDesc')} &bull; <strong className="text-foreground">{setup.primaryKeyword || '—'}</strong>
          </p>
        </div>

        <button
          type="button"
          disabled={isGeneratingTitles}
          onClick={generateTitles}
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs font-bold hover:bg-muted text-foreground transition-colors disabled:opacity-50"
        >
          <RotateCcw className={`w-3.5 h-3.5 ${isGeneratingTitles ? 'animate-spin' : ''}`} />
          <span>{isGeneratingTitles ? t('wizard.step2Details.generatingTitles') : t('wizard.step2Details.regenerateBtn')}</span>
        </button>
      </div>

      {/* 2. 5 SEO Title Selection Cards */}
      <div className="space-y-3">
        {generatedTitles.length === 0 ? (
          <div className="p-8 text-center rounded-2xl border border-surface-border bg-surface">
            <Sparkles className="w-8 h-8 text-primary mx-auto mb-2 animate-bounce" />
            <p className="text-sm font-medium text-foreground">{t('wizard.step2Details.generatingTitles')}</p>
            <button
              type="button"
              onClick={generateTitles}
              className="mt-3 px-4 py-2 rounded-xl bg-primary text-primary-fg text-xs font-bold"
            >
              {t('wizard.step2Details.regenerateBtn')}
            </button>
          </div>
        ) : (
          generatedTitles.map((item) => {
            const isSelected = selectedTitleId === item.id;
            const badgeInfo = getScoreBadge(item.seoScore);

            return (
              <div
                key={item.id}
                onClick={() => handleSelectTitleCard(item.id)}
                className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer flex items-start gap-4 ${
                  isSelected
                    ? 'border-primary bg-primary/10 ring-1 ring-primary/40 shadow-glow-primary/20'
                    : 'border-surface-border bg-surface hover:border-surface-border/80 hover:bg-surface-elevated/50'
                }`}
              >
                {/* Radio selection icon */}
                <div className="mt-1 flex-shrink-0">
                  {isSelected ? (
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  ) : (
                    <Circle className="w-5 h-5 text-foreground-muted/40" />
                  )}
                </div>

                {/* Content details */}
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Angle tag */}
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-surface-elevated border border-surface-border text-foreground-muted">
                      {item.angle}
                    </span>

                    {/* SEO Score Badge */}
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold border ${badgeInfo.bg}`}
                    >
                      {item.seoScore}/100 • {badgeInfo.label}
                    </span>

                    {/* Character Count */}
                    <span className="text-[10px] font-mono text-foreground-muted ml-auto">
                      {item.characterCount} {t('editor.chars')}
                    </span>
                  </div>

                  {/* Title Text */}
                  <div className="font-heading font-extrabold text-sm sm:text-base text-foreground leading-snug">
                    {item.title}
                  </div>

                  {/* Rationale */}
                  <p className="text-[11px] text-foreground-muted leading-relaxed">
                    💡 {item.rationale}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 3. Custom Title Editor & Live Optimizer */}
      <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-glass space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-surface-border">
          <div className="flex items-center gap-2">
            <FileEdit className="w-5 h-5 text-primary" />
            <h4 className="font-heading font-bold text-sm text-foreground">
              {t('wizard.step2Details.customTitleLabel')}
            </h4>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono text-foreground-muted">
              {customTitle.length} {t('editor.chars')}
            </span>
            <span
              className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold border ${
                getScoreBadge(liveScore).bg
              }`}
            >
              {t('wizard.step2Details.liveScoreLabel')} {liveScore}/100
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <input
            type="text"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
            placeholder={t('wizard.step2Details.customTitlePlaceholder')}
            className="w-full px-4 py-3 rounded-xl border border-surface-border bg-surface-elevated text-sm font-heading font-bold text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all placeholder:text-foreground-muted/60"
          />
          <div className="flex items-center justify-between text-[11px] text-foreground-muted">
            <span className="flex items-center gap-1">
              <Zap className="w-3.5 h-3.5 text-accent" />
              <span>45 - 65 chars recommended for optimal CTR on Google & AEO engines.</span>
            </span>
          </div>
        </div>
      </div>

      {/* 4. Navigation & Export Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={() => setStep(1)}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-surface-border bg-surface-elevated text-xs font-bold hover:bg-muted text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('common.back')}</span>
        </button>

        <div className="w-full sm:w-auto flex items-center gap-3">
          {onPushToEditor && (
            <button
              type="button"
              onClick={handlePushDirectlyToEditor}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-primary/30 bg-primary/10 text-primary text-xs font-bold hover:bg-primary/20 transition-colors"
            >
              <span>{t('wizard.step2Details.pushToEditorBtn')}</span>
            </button>
          )}

          <button
            type="button"
            disabled={!customTitle.trim()}
            onClick={handleContinueToStep3}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-primary-fg text-xs font-bold hover:bg-primary-hover shadow-glow-primary/20 transition-all active:scale-95 disabled:opacity-50"
          >
            <span>{t('wizard.step2Details.continueBtn')}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
